import { useEffect } from "react";

import { useCartActions } from "../../contexts/CartServiceContext/CartServiceContext";
import {
	getPrevUid,
	setPrevUid,
	shouldClearCart,
} from "../../contexts/CartServiceContext/uidHelper";
import { LINKPAY_COOKIES } from "../../pages/constants";
import { useCookie } from "./useCookie";

/**
 * Hook to track user UID changes and manage cart accordingly
 *
 * User state transitions and cart behavior:
 * 1. Guest -> User (user login): Keep cart (inherit guest cart)
 * 2. User A -> User B (user switch): Clear cart
 * 3. User -> Guest (user logout): Clear cart
 * 4. Guest -> Guest: Keep cart
 * 5. User -> User (same user): Keep cart
 */
export const useCartUserTracking = () => {
	const cartActions = useCartActions();
	const { cookieValue: uid } = useCookie<string>(LINKPAY_COOKIES.UID);

	useEffect(() => {
		const prevUid = getPrevUid();
		const currentUid = uid || null;

		// Determine if cart should be cleared based on user state transition
		if (shouldClearCart(currentUid, prevUid)) {
			// Cases requiring cart clearing:
			// - User A -> User B (user switch)
			// - User -> Guest (user logout)
			cartActions.CLEAN_CART();
		}
		// Cases keeping cart:
		// - Guest -> User (user login - inherit guest cart)
		// - Guest -> Guest (no change)
		// - User -> User (same user)

		// Update stored UID for next comparison
		setPrevUid(currentUid);
	}, [uid, cartActions]);

	return {
		currentUid: uid || null,
		prevUid: getPrevUid(),
	};
};
